import type {
  BugCatchOptions,
  UserContext,
  BreadcrumbEntry,
  EventPayload,
  MetricPayload,
  ParsedDsn,
  ExceptionValue,
  LogLevel,
  LogPayload,
  TransactionPayload,
  SessionPayload,
  SessionStatus,
  WebVitalPayload,
  WebVitalMetric,
  ReplayChunkPayload,
} from './types.js';
import { parseStack } from './parse-stack.js';
import {
  BreadcrumbBuffer,
  installNavigationBreadcrumbs,
  installClickBreadcrumbs,
  installConsoleBreadcrumbs,
} from './breadcrumbs.js';
import { BugCatchTransaction } from './tracing.js';

// ─── UUID ─────────────────────────────────────────────────────────────────────

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Session status severity ────────────────────────────────────────────────

const SESSION_SEVERITY: Record<SessionStatus, number> = { ok: 0, errored: 1, crashed: 2 };

/** Marks the handler an error came through - unhandled ones crash the session, manual `captureException()` calls only mark it "errored". */
const FATAL_HANDLERS = new Set([
  'uncaughtException',
  'unhandledRejection',
  'window.onerror',
  'unhandledrejection',
]);

// ─── DSN parser ───────────────────────────────────────────────────────────────

function parseDsn(
  dsn: string,
): ParsedDsn & {
  metricsUrl: string;
  logsUrl: string;
  transactionsUrl: string;
  sessionsUrl: string;
  webVitalsUrl: string;
  replayUrl: string;
  replayAllowedUrl: string;
} {
  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    throw new Error(`[BugCatch] Invalid DSN: "${dsn}"`);
  }

  const sdkKey = url.searchParams.get('key');
  if (!sdkKey) {
    throw new Error(`[BugCatch] DSN is missing the "key" query parameter: "${dsn}"`);
  }

  // Extract projectId from the last path segment (/ingest/{projectId})
  const segments = url.pathname.split('/').filter(Boolean);
  const projectId = segments[segments.length - 1];
  if (!projectId) {
    throw new Error(`[BugCatch] Could not extract projectId from DSN path: "${dsn}"`);
  }

  // Reconstruct the URL without extra query params
  const ingestUrl = `${url.origin}${url.pathname}?key=${sdkKey}`;
  const metricsUrl = `${url.origin}${url.pathname}/metrics?key=${sdkKey}`;
  const logsUrl = `${url.origin}${url.pathname}/logs?key=${sdkKey}`;
  const transactionsUrl = `${url.origin}${url.pathname}/transactions?key=${sdkKey}`;
  const sessionsUrl = `${url.origin}${url.pathname}/sessions?key=${sdkKey}`;
  const webVitalsUrl = `${url.origin}${url.pathname}/web-vitals?key=${sdkKey}`;
  const replayUrl = `${url.origin}${url.pathname}/replay?key=${sdkKey}`;
  const replayAllowedUrl = `${url.origin}${url.pathname}/replay-allowed?key=${sdkKey}`;

  return {
    ingestUrl,
    metricsUrl,
    logsUrl,
    transactionsUrl,
    sessionsUrl,
    webVitalsUrl,
    replayUrl,
    replayAllowedUrl,
    projectId,
    sdkKey,
  };
}

// ─── Route normalizer ─────────────────────────────────────────────────────────

/**
 * Strips the origin and query string from a URL and replaces dynamic path
 * segments (UUIDs, numeric IDs) with `:id` so metrics group by route template.
 *
 * e.g. https://api.example.com/users/42/orders/abc-123?page=1 → /users/:id/orders/:id
 */
function normalizeUrl(rawUrl: string): string {
  let pathname: string;
  try {
    pathname = new URL(rawUrl).pathname;
  } catch {
    // rawUrl is already a path (e.g. "/api/users/1")
    pathname = rawUrl.split('?')[0];
  }
  return pathname
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d{1,20}(?=\/|$)/g, '/:id');
}

// ─── Pattern matching ─────────────────────────────────────────────────────────

function matchesPattern(value: string, patterns: Array<string | RegExp>): boolean {
  return patterns.some((p) =>
    typeof p === 'string' ? value.includes(p) : p.test(value),
  );
}

// ─── String truncation (span descriptions) ─────────────────────────────────────

function truncate(value: string, max = 300): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

// ─── Client ───────────────────────────────────────────────────────────────────

export class BugCatchClient {
  private readonly opts: Required<
    Pick<
      BugCatchOptions,
      | 'debug'
      | 'maxBreadcrumbs'
      | 'autoCaptureErrors'
      | 'autoCaptureBreadcrumbs'
      | 'autoTrackRequests'
      | 'autoCaptureLogs'
      | 'logsFlushInterval'
      | 'logsBatchSize'
      | 'tracesFlushInterval'
      | 'tracesBatchSize'
      | 'autoSessionTracking'
      | 'sessionsFlushInterval'
      | 'autoCaptureWebVitals'
      | 'webVitalsFlushInterval'
      | 'sessionReplay'
      | 'replayFlushInterval'
      | 'replayRequestTimeout'
    >
  > & BugCatchOptions;

  private readonly dsn: ParsedDsn & {
    metricsUrl: string;
    logsUrl: string;
    transactionsUrl: string;
    sessionsUrl: string;
    webVitalsUrl: string;
    replayUrl: string;
    replayAllowedUrl: string;
  };
  private readonly crumbs: BreadcrumbBuffer;
  private user: UserContext = {};
  private tags: Record<string, string> = {};
  private currentTraceId: string | undefined;
  private currentTransaction: BugCatchTransaction | undefined;
  private readonly logQueue: LogPayload[] = [];
  private logFlushTimer: ReturnType<typeof setInterval> | undefined;
  private readonly transactionQueue: TransactionPayload[] = [];
  private transactionFlushTimer: ReturnType<typeof setInterval> | undefined;
  private readonly sessionQueue: SessionPayload[] = [];
  private sessionFlushTimer: ReturnType<typeof setInterval> | undefined;
  private sessionId: string | undefined;
  private sessionStartedAt: string | undefined;
  private sessionStatus: SessionStatus = 'ok';
  private sessionErrorCount = 0;
  private readonly webVitalsQueue: WebVitalPayload[] = [];
  private webVitalsFlushTimer: ReturnType<typeof setInterval> | undefined;
  private readonly replayQueue: unknown[] = [];
  private replayFlushTimer: ReturnType<typeof setInterval> | undefined;
  private replayId: string | undefined;
  private replaySequence = 0;
  private replayStartedAt: string | undefined;
  /** True while an `isReplayAllowed()` check for a pending start is in flight. */
  private replayStarting = false;
  /** Set if `stopReplay()` runs while a start's allowlist check is still in flight, so the check's callback aborts instead of starting a recording that was already told to stop. */
  private replayStopRequested = false;
  /** Guards against concurrent replay POSTs - a busy page can refill 200 events
   *  faster than one large chunk round-trips, and unbounded parallel sends
   *  can exhaust the browser's per-origin connection limit and stall every
   *  other request the page makes. */
  private replaySendInFlight = false;
  private replayFlushPending = false;
  private stopRecording: (() => void) | undefined;
  private readonly cleanups: Array<() => void> = [];

  constructor(options: BugCatchOptions) {
    this.opts = {
      debug: false,
      maxBreadcrumbs: 100,
      autoCaptureErrors: true,
      autoCaptureBreadcrumbs: true,
      autoTrackRequests: false,
      autoCaptureLogs: false,
      logsFlushInterval: 2000,
      logsBatchSize: 20,
      tracesFlushInterval: 2000,
      tracesBatchSize: 10,
      autoSessionTracking: true,
      sessionsFlushInterval: 5000,
      autoCaptureWebVitals: true,
      webVitalsFlushInterval: 5000,
      sessionReplay: false,
      replayFlushInterval: 10000,
      replayRequestTimeout: 15000,
      ...options,
    };

    this.dsn = parseDsn(options.dsn);
    this.crumbs = new BreadcrumbBuffer(this.opts.maxBreadcrumbs);

    if (this.opts.autoCaptureErrors) {
      this.installErrorHandlers();
    }

    if (this.opts.autoCaptureBreadcrumbs) {
      this.cleanups.push(installNavigationBreadcrumbs((c) => this.addBreadcrumb(c)));
      this.cleanups.push(installClickBreadcrumbs((c) => this.addBreadcrumb(c)));
      this.cleanups.push(installConsoleBreadcrumbs((c) => this.addBreadcrumb(c)));
    }

    if (this.opts.autoTrackRequests) {
      this.cleanups.push(this.installFetchInterceptor());
      this.cleanups.push(this.installXhrInterceptor());
    }

    if (this.opts.autoCaptureLogs) {
      this.cleanups.push(this.installConsoleLogCapture());
    }

    // The flush timer runs regardless of autoCaptureLogs - captureLog() is a
    // public method callers can use directly without the console hook.
    this.logFlushTimer = setInterval(() => void this.flushLogs(), this.opts.logsFlushInterval);
    this.cleanups.push(() => clearInterval(this.logFlushTimer));

    this.transactionFlushTimer = setInterval(
      () => void this.flushTransactions(),
      this.opts.tracesFlushInterval,
    );
    this.cleanups.push(() => clearInterval(this.transactionFlushTimer));

    if (this.opts.autoSessionTracking) {
      this.startSession();
    }
    this.sessionFlushTimer = setInterval(
      () => void this.flushSessions(),
      this.opts.sessionsFlushInterval,
    );
    this.cleanups.push(() => clearInterval(this.sessionFlushTimer));

    if (this.opts.autoCaptureWebVitals && typeof window !== 'undefined') {
      this.installWebVitalsCapture();
    }
    this.webVitalsFlushTimer = setInterval(
      () => void this.flushWebVitals(),
      this.opts.webVitalsFlushInterval,
    );
    this.cleanups.push(() => clearInterval(this.webVitalsFlushTimer));

    if (this.opts.sessionReplay && typeof window !== 'undefined') {
      this.startReplay();
    }
    this.replayFlushTimer = setInterval(
      () => void this.flushReplay(),
      this.opts.replayFlushInterval,
    );
    this.cleanups.push(() => clearInterval(this.replayFlushTimer));

    if (typeof window !== 'undefined') {
      // beacon, not fetch - the tab is closing, there's no time for a normal
      // request/response round trip.
      const onPageHide = () => {
        this.endSession();
        void this.flushLogs(true);
        void this.flushTransactions(true);
        void this.flushSessions(true);
        void this.flushWebVitals(true);
        this.stopReplay(true);
      };
      window.addEventListener('pagehide', onPageHide);
      this.cleanups.push(() => window.removeEventListener('pagehide', onPageHide));
    } else if (this.opts.autoSessionTracking) {
      // Node has no pagehide - `beforeExit` fires when the event loop is
      // about to end naturally. Doesn't catch SIGKILL or a direct
      // process.exit() call, but that's true of any flush-on-shutdown hook;
      // destroy() covers explicit teardown.
      const onBeforeExit = () => {
        this.endSession();
        void this.flushSessions();
      };
      process.on('beforeExit', onBeforeExit);
      this.cleanups.push(() => process.off('beforeExit', onBeforeExit));
    }

    this.log('Initialized. Project:', this.dsn.projectId);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  captureException(error: unknown, extra?: Record<string, unknown>): string {
    // Auto-tagged, not something callers pass themselves - lets the
    // dashboard jump from an error straight to "what led to this" without
    // any manual plumbing at the call site.
    const taggedExtra = this.replayId ? { ...extra, replay_id: this.replayId } : extra;
    const event = this.buildExceptionPayload(error, taggedExtra);
    void this.send(event);
    const fatal = typeof extra?.handler === 'string' && FATAL_HANDLERS.has(extra.handler);
    this.recordSessionError(fatal);
    return event.event_id;
  }

  captureMessage(message: string, level: string = 'info', extra?: Record<string, unknown>): string {
    const event = this.buildMessagePayload(message, level, extra);
    void this.send(event);
    return event.event_id;
  }

  /**
   * Queue a structured log line. Batched and flushed automatically - does
   * not send a request per call. `traceId` overrides the trace set via
   * `setTraceId()` for this one line.
   */
  captureLog(
    level: LogLevel,
    message: string,
    attributes?: Record<string, unknown>,
    traceId?: string,
  ): void {
    this.logQueue.push({
      level,
      message,
      timestamp: new Date().toISOString(),
      trace_id: traceId ?? this.currentTraceId,
      attributes,
      environment: this.opts.environment,
      release: this.opts.release,
    });

    if (this.logQueue.length >= this.opts.logsBatchSize) {
      void this.flushLogs();
    }
  }

  /**
   * Set the trace id attached to every `captureLog()` call that doesn't pass
   * its own. Global mutable state, same as `setUser` - fine for a browser
   * session, but a Node server handling concurrent requests should pass
   * `traceId` explicitly to `captureLog()` instead of relying on this.
   */
  setTraceId(traceId: string): void {
    this.currentTraceId = traceId;
  }

  clearTraceId(): void {
    this.currentTraceId = undefined;
  }

  /** Send any queued log lines immediately instead of waiting for the next flush. */
  flushLogsNow(): Promise<void> {
    return this.flushLogs();
  }

  /** Send any queued session updates immediately instead of waiting for the next flush. */
  flushSessionsNow(): Promise<void> {
    return this.flushSessions();
  }

  /** Send any queued Web Vitals readings immediately instead of waiting for the next flush. */
  flushWebVitalsNow(): Promise<void> {
    return this.flushWebVitals();
  }

  /**
   * Start recording session replay if it isn't already running. Only needed
   * when `sessionReplay: true` wasn't set at `init()` time - e.g. starting a
   * recording only after a user opts in, or only for a sampled subset of
   * sessions decided by the calling app. No-op outside the browser.
   */
  startReplayNow(): void {
    if (typeof window === 'undefined' || this.replayId) return;
    this.startReplay();
  }

  /** Stop the active recording (if any) and flush whatever's queued. */
  stopReplayNow(): void {
    this.stopReplay();
    void this.flushReplay();
  }

  /** Send any queued replay chunks immediately instead of waiting for the next flush. */
  flushReplayNow(): Promise<void> {
    return this.flushReplay();
  }

  /** The active replay recording's id, if session replay is currently running. */
  getReplayId(): string | undefined {
    return this.replayId;
  }

  /**
   * Start a new traced transaction. Queued for delivery when `.finish()` is
   * called on it - starting one and never finishing it leaks nothing (no
   * request is made), it's just never reported.
   *
   * @example
   * const txn = BugCatch.startTransaction('GET /api/orders/:id', 'http.server');
   * const span = txn.startChild('db.query', 'SELECT * FROM orders WHERE id = ?');
   * // ...
   * span.finish();
   * txn.setHttpStatus(200);
   * txn.finish();
   */
  startTransaction(name: string, op = 'custom'): BugCatchTransaction {
    return new BugCatchTransaction(name, op, (txn) => this.captureTransaction(txn));
  }

  /**
   * Set the transaction `startTransaction()`'s auto-instrumentation (the
   * fetch/XHR interceptors under `autoTrackRequests`) attaches child spans
   * to. Global mutable state, same limitation as `setTraceId` - a Node
   * server handling concurrent requests should keep its own reference to
   * the transaction (e.g. on `req`) and call `.startChild()` on it directly
   * instead of relying on this.
   */
  setCurrentTransaction(transaction: BugCatchTransaction | undefined): void {
    this.currentTransaction = transaction;
  }

  getCurrentTransaction(): BugCatchTransaction | undefined {
    return this.currentTransaction;
  }

  /**
   * Queue a finished transaction for delivery. Called automatically by
   * `transaction.finish()` - only call this directly if you built the
   * transaction payload yourself instead of using `startTransaction()`.
   */
  captureTransaction(transaction: BugCatchTransaction): void {
    this.transactionQueue.push(
      transaction.toPayload(this.user.id, this.opts.environment, this.opts.release),
    );
    if (this.transactionQueue.length >= this.opts.tracesBatchSize) {
      void this.flushTransactions();
    }
  }

  /** Send any queued transactions immediately instead of waiting for the next flush. */
  flushTransactionsNow(): Promise<void> {
    return this.flushTransactions();
  }

  /**
   * Express/Connect-style middleware: starts an `http.server` transaction
   * per request, finishes it when the response ends. Attaches the
   * transaction to `req.bugcatchTransaction` for concurrency-safe access,
   * and also sets it as the "current" transaction (see `setCurrentTransaction`)
   * as a convenience for single-request-at-a-time contexts.
   *
   * By default, requests that end in a 404 are dropped rather than
   * captured - a "route not found" is rarely a real business transaction
   * (bots/scanners probing random paths are a common source), and letting
   * them through skews throughput/error-rate stats and clutters the
   * transaction list with noise nobody asked to track. Pass
   * `ignoreStatusCodes: []` to capture everything, or a different list to
   * customize it.
   *
   * `ignoreRoutes` is the explicit version of the same idea - name specific
   * paths (health checks, readiness probes, ...) that were never a "real"
   * request to begin with, so they're skipped before a transaction is even
   * started (no wasted work, and any db.query/etc spans during the request
   * just have no parent transaction to attach to, same as when tracing
   * isn't active at all). Matched against the same normalized route
   * (`/users/:id`, not the raw URL) used for the transaction name - a
   * string matches by substring, a RegExp by `.test()`, same as
   * `ignoreUrls`/`ignoreErrors`.
   *
   * @example
   * app.use(BugCatch.tracingMiddleware());
   * app.use(BugCatch.tracingMiddleware({ ignoreStatusCodes: [404, 401] }));
   * app.use(BugCatch.tracingMiddleware({ ignoreRoutes: ['/health', /^\/internal\//] }));
   */
  tracingMiddleware(options?: { ignoreStatusCodes?: number[]; ignoreRoutes?: Array<string | RegExp> }) {
    const ignoreStatusCodes = new Set(options?.ignoreStatusCodes ?? [404]);
    const ignoreRoutes = options?.ignoreRoutes ?? [];

    return (
      req: { method: string; originalUrl?: string; url?: string; bugcatchTransaction?: BugCatchTransaction },
      res: { statusCode: number; on: (event: 'finish', cb: () => void) => void },
      next: () => void,
    ) => {
      const route = normalizeUrl(req.originalUrl ?? req.url ?? '/');

      if (ignoreRoutes.length > 0 && matchesPattern(route, ignoreRoutes)) {
        next();
        return;
      }

      const txn = this.startTransaction(`${req.method} ${route}`, 'http.server');
      req.bugcatchTransaction = txn;
      this.setCurrentTransaction(txn);

      res.on('finish', () => {
        if (this.currentTransaction === txn) this.setCurrentTransaction(undefined);
        // Not txn.finish() - that unconditionally queues the transaction for
        // delivery. Dropping it here means it's simply never sent.
        if (ignoreStatusCodes.has(res.statusCode)) return;
        txn.setHttpStatus(res.statusCode);
        txn.finish();
      });

      next();
    };
  }

  /**
   * Patch TypeORM's `QueryRunner.query()` (the single choke point every
   * repository/query-builder call eventually goes through) so each query
   * becomes a `db.query` child span on `currentTransaction` - same global
   * mutable state `tracingMiddleware()` sets, same caveat as `setTraceId`
   * for concurrent servers.
   *
   * TypeORM's `QueryRunner` export is a TypeScript **interface**, not a
   * class - there is no `.prototype` to patch on it directly. The concrete
   * class is driver-specific (`MysqlQueryRunner`, `PostgresQueryRunner`,
   * ...) and isn't exported. So this takes the `DataSource` instead, spins
   * up one query runner to discover its (shared, per-driver) prototype via
   * `Object.getPrototypeOf`, patches `.query` there - which affects every
   * runner of that driver, past or future, since they all share the same
   * class - and releases the probe runner. Duck-typed against
   * `{ createQueryRunner }` so this package never needs `typeorm` as a
   * dependency.
   *
   * @example
   * BugCatch.instrumentTypeOrm(dataSource); // the TypeORM DataSource
   */
  instrumentTypeOrm(dataSource: {
    createQueryRunner: () => {
      // Method-shorthand syntax (not `query: (...args) => ...`) so
      // TypeScript checks this bivariantly - a property-style function type
      // is checked strictly contravariantly, which real QueryRunner.query
      // overloads (newer typeorm versions add more of them) can fail against
      // even though they're perfectly callable with `...args: unknown[]`.
      query(...args: unknown[]): Promise<unknown>;
      release?: () => unknown;
    };
  }): () => void {
    const probe = dataSource.createQueryRunner();
    const proto = Object.getPrototypeOf(probe) as {
      query(...args: unknown[]): Promise<unknown>;
    };
    const original = proto.query;
    const self = this;

    proto.query = function (this: unknown, sql: unknown, ...rest: unknown[]) {
      const span = self.currentTransaction?.startChild(
        'db.query',
        typeof sql === 'string' ? truncate(sql) : undefined,
      );
      return Promise.resolve(original.apply(this, [sql, ...rest])).then(
        (value) => {
          span?.finish('ok');
          return value;
        },
        (err: unknown) => {
          span?.finish('error');
          throw err;
        },
      );
    };

    void Promise.resolve(probe.release?.()).catch(() => {});

    this.log('instrumentTypeOrm: QueryRunner.prototype.query patched');

    return () => {
      proto.query = original;
      this.log('instrumentTypeOrm: QueryRunner.prototype.query restored');
    };
  }

  /**
   * Patch ioredis's `sendCommand()` (every command - `get`, `set`, `hset`, a
   * pipeline's individual calls - funnels through it) so each Redis command
   * becomes a `cache.command` child span on `currentTransaction`. Same
   * global-mutable-state caveat as `instrumentTypeOrm`/`tracingMiddleware`.
   * Duck-typed against `{ prototype: { sendCommand } }` so this package
   * never needs `ioredis` as a dependency.
   *
   * @example
   * import Redis from 'ioredis';
   * BugCatch.instrumentIoredis(Redis);
   */
  instrumentIoredis(RedisClass: {
    // Method-shorthand syntax, same reasoning as instrumentTypeOrm()'s
    // `query` - keeps this bivariantly checked against ioredis's real
    // (overloaded) `sendCommand` signature instead of strict contravariance.
    prototype: { sendCommand(...args: unknown[]): unknown };
  }): () => void {
    const proto = RedisClass.prototype;
    const original = proto.sendCommand;
    const self = this;

    proto.sendCommand = function (this: unknown, command: unknown, ...rest: unknown[]) {
      const cmd = command as { name?: string; args?: unknown[]; promise?: Promise<unknown> };
      const description = [cmd?.name, ...(cmd?.args ?? [])]
        .filter((v) => v !== undefined)
        .map(String)
        .join(' ');
      const span = self.currentTransaction?.startChild(
        'cache.command',
        description ? truncate(description) : undefined,
      );

      const result = original.apply(this, [command, ...rest]);
      const promise = cmd?.promise ?? result;
      if (promise && typeof (promise as Promise<unknown>).then === 'function') {
        (promise as Promise<unknown>).then(
          () => span?.finish('ok'),
          () => span?.finish('error'),
        );
      } else {
        span?.finish('ok');
      }
      return result;
    };

    this.log('instrumentIoredis: Redis.prototype.sendCommand patched');

    return () => {
      proto.sendCommand = original;
      this.log('instrumentIoredis: Redis.prototype.sendCommand restored');
    };
  }

  setUser(user: UserContext): void {
    this.user = {
      ...user,
      id: user.id != null ? String(user.id) : undefined,
    };
  }

  clearUser(): void {
    this.user = {};
  }

  setTag(key: string, value: string): void {
    this.tags[key] = value;
  }

  addBreadcrumb(crumb: BreadcrumbEntry): void {
    this.crumbs.add(crumb);
  }

  /** Tear down all global listeners. Call when unmounting in SPAs. */
  destroy(): void {
    for (const cleanup of this.cleanups) {
      cleanup();
    }
    this.cleanups.length = 0;
    this.endSession();
    void this.flushLogs();
    void this.flushTransactions();
    void this.flushSessions();
    void this.flushWebVitals();
    this.stopReplay();
    void this.flushReplay();
  }

  /**
   * Manually report an API call timing. Use this when `autoTrackRequests`
   * is off or when you want to track server-side (Node.js) requests explicitly.
   */
  trackRequest(method: string, route: string, durationMs: number, statusCode: number): void {
    void this.sendMetric({ method: method.toUpperCase(), route, duration_ms: durationMs, status_code: statusCode });
  }

  // ─── Internal: build payloads ────────────────────────────────────────────

  private buildExceptionPayload(
    error: unknown,
    extra?: Record<string, unknown>,
  ): EventPayload {
    const err = error instanceof Error ? error : new Error(String(error));
    const frames = parseStack(err);

    const exceptionValue: ExceptionValue = {
      type: err.name || 'Error',
      value: err.message,
      stacktrace: frames.length > 0 ? { frames } : undefined,
    };

    return this.buildBase({
      level: 'error',
      exception: { values: [exceptionValue] },
      extra,
    });
  }

  private buildMessagePayload(
    message: string,
    level: string,
    extra?: Record<string, unknown>,
  ): EventPayload {
    return this.buildBase({ level, message, extra });
  }

  private buildBase(
    overrides: Partial<EventPayload> & { extra?: Record<string, unknown> },
  ): EventPayload {
    const { extra, ...rest } = overrides;
    const payload: EventPayload = {
      event_id: uuid(),
      timestamp: new Date().toISOString(),
      platform: 'javascript',
      ...rest,
    };

    if (this.opts.release) payload.release = this.opts.release;
    if (this.opts.environment) payload.environment = this.opts.environment;

    // `user`/`tags`/`request` inside `extra` are hoisted to their real
    // top-level payload fields instead of being buried under `extra`, which
    // the ingest API never reads them from. A caller coming from Sentry
    // naturally reaches for `captureException(err, { user, tags, request })`
    // expecting exactly this - and for a concurrent Node server, passing
    // `user`/`request` per call here (rather than through the global,
    // request-unsafe `setUser()`) is the *only* way to get a per-request
    // `user.ip_address` into an event at all, which is what the ingest API
    // prioritizes over its own IP detection for exactly this reason.
    const { user: extraUser, tags: extraTags, request: extraRequest, ...restExtra } =
      (extra ?? {}) as {
        user?: UserContext;
        tags?: Record<string, string>;
        request?: EventPayload['request'];
        [key: string]: unknown;
      };

    const mergedUser = { ...this.user, ...extraUser };
    if (Object.keys(mergedUser).length > 0) {
      payload.user = mergedUser;
    }

    const mergedTags = { ...this.tags, ...extraTags };
    if (Object.keys(mergedTags).length > 0) {
      payload.tags = mergedTags;
    }

    if (Object.keys(restExtra).length > 0) {
      payload.extra = restExtra;
    }

    const breadcrumbs = this.crumbs.getAll();
    if (breadcrumbs.length > 0) {
      payload.breadcrumbs = breadcrumbs;
    }

    // Capture browser request context - a caller-supplied `request` (Node,
    // no `window`) takes over instead of being silently dropped.
    if (typeof window !== 'undefined') {
      payload.request = {
        url: window.location.href,
        headers: {
          'user-agent': navigator.userAgent,
        },
      };
    } else if (extraRequest) {
      payload.request = extraRequest;
    }

    return payload;
  }

  // ─── Internal: error handlers ────────────────────────────────────────────

  private installErrorHandlers(): void {
    if (typeof window === 'undefined') {
      // Node.js environment
      const onUncaught = (err: Error) => {
        this.captureException(err, { handler: 'uncaughtException' });
      };
      const onUnhandled = (reason: unknown) => {
        this.captureException(reason, { handler: 'unhandledRejection' });
      };
      process.on('uncaughtException', onUncaught);
      process.on('unhandledRejection', onUnhandled);
      this.cleanups.push(() => {
        process.off('uncaughtException', onUncaught);
        process.off('unhandledRejection', onUnhandled);
      });
      return;
    }

    // Browser environment
    const onError = (event: ErrorEvent) => {
      if (!event.error) return;
      if (this.shouldIgnore(event.error, event.filename ?? '')) return;
      this.captureException(event.error, { handler: 'window.onerror' });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      if (this.shouldIgnore(reason, '')) return;
      this.captureException(reason, { handler: 'unhandledrejection' });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    this.cleanups.push(() => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    });
  }

  private shouldIgnore(error: unknown, scriptUrl: string): boolean {
    const { ignoreUrls, ignoreErrors } = this.opts;

    if (ignoreUrls?.length && scriptUrl) {
      if (matchesPattern(scriptUrl, ignoreUrls)) return true;
    }

    if (ignoreErrors?.length) {
      const message = error instanceof Error ? error.message : String(error);
      if (matchesPattern(message, ignoreErrors)) return true;
    }

    return false;
  }

  // ─── Internal: request tracking ──────────────────────────────────────────

  private installFetchInterceptor(): () => void {
    if (typeof globalThis.fetch !== 'function') {
      console.warn('[BugCatch] autoTrackRequests is enabled but globalThis.fetch is not available. Metrics will not be tracked.');
      return () => {};
    }

    const original = globalThis.fetch.bind(globalThis);
    const self = this;

    this.log('autoTrackRequests: fetch interceptor installed');

    globalThis.fetch = async function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url = input instanceof Request ? input.url : String(input);

      // Never intercept BugCatch's own ingest/metrics requests
      if (url.includes(self.dsn.ingestUrl.split('?')[0])) {
        return original(input, init);
      }

      if (self.opts.trackIgnoreUrls && matchesPattern(url, self.opts.trackIgnoreUrls)) {
        self.log('autoTrackRequests: ignored (trackIgnoreUrls match)', url);
        return original(input, init);
      }

      const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
      const route = normalizeUrl(url);
      const start = Date.now();
      const span = self.currentTransaction?.startChild('http.client', `${method} ${route}`);

      self.log('autoTrackRequests: intercepted', method, url);

      try {
        const response = await original(input, init);
        void self.sendMetric({
          method,
          route,
          duration_ms: Date.now() - start,
          status_code: response.status,
          user_id: self.user.id,
        });
        span?.finish(response.ok ? 'ok' : 'error');
        return response;
      } catch (err) {
        // Network error - report as status 0
        void self.sendMetric({ method, route, duration_ms: Date.now() - start, status_code: 0 });
        span?.finish('error');
        throw err;
      }
    };

    // Return cleanup function that restores original fetch
    return () => {
      globalThis.fetch = original;
      this.log('autoTrackRequests: fetch interceptor removed');
    };
  }

  private installXhrInterceptor(): () => void {
    if (typeof XMLHttpRequest === 'undefined') {
      // Node.js environment - XHR not available
      return () => {};
    }

    const self = this;
    const OriginalXHR = XMLHttpRequest;
    const ingestBase = this.dsn.ingestUrl.split('?')[0];

    function PatchedXHR(this: XMLHttpRequest) {
      const xhr = new OriginalXHR();

      let method = 'GET';
      let url = '';
      let start = 0;

      const originalOpen = xhr.open.bind(xhr);
      const originalSend = xhr.send.bind(xhr);

      (this as any).open = function (m: string, u: string, async?: boolean, user?: string, password?: string) {
        method = m ? m.toUpperCase() : 'GET';
        url = u || '';
        return originalOpen(m, u, async ?? true, user, password);
      };

      (this as any).send = function (body?: Document | XMLHttpRequestBodyInit | null) {
        // Skip BugCatch's own requests and ignored URLs
        const skip =
          url.includes(ingestBase) ||
          (self.opts.trackIgnoreUrls ? matchesPattern(url, self.opts.trackIgnoreUrls) : false);

        if (!skip) {
          start = Date.now();
          const route = normalizeUrl(url);
          const span = self.currentTransaction?.startChild('http.client', `${method} ${route}`);
          self.log('autoTrackRequests: XHR intercepted', method, url);

          xhr.addEventListener('loadend', () => {
            void self.sendMetric({
              method,
              route,
              duration_ms: Date.now() - start,
              status_code: xhr.status || 0,
              user_id: self.user.id,
            });
            span?.finish(xhr.status && xhr.status < 400 ? 'ok' : 'error');
          });
        }

        return originalSend(body);
      };

      // Proxy all other properties/methods to the real XHR instance
      return new Proxy(this, {
        get(_target, prop) {
          if (prop === 'open') return (this as any).open;
          if (prop === 'send') return (this as any).send;
          const val = (xhr as any)[prop];
          return typeof val === 'function' ? val.bind(xhr) : val;
        },
        set(_target, prop, value) {
          (xhr as any)[prop] = value;
          return true;
        },
      });
    }

    PatchedXHR.prototype = OriginalXHR.prototype;
    (window as any).XMLHttpRequest = PatchedXHR;

    this.log('autoTrackRequests: XHR interceptor installed');

    return () => {
      (window as any).XMLHttpRequest = OriginalXHR;
      this.log('autoTrackRequests: XHR interceptor removed');
    };
  }

  private async sendMetric(metric: MetricPayload): Promise<void> {
    this.log('autoTrackRequests: sending metric', metric.method, metric.route, metric.duration_ms + 'ms', metric.status_code);
    try {
      const res = await fetch(this.dsn.metricsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metric),
      });
      if (!res.ok) {
        this.log('autoTrackRequests: metric rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('autoTrackRequests: failed to send metric -', (err as Error).message);
    }
  }

  // ─── Internal: logs ───────────────────────────────────────────────────────

  /**
   * Patches console.trace/debug/log/info/warn/error to also queue a
   * structured log. Composes with `installConsoleBreadcrumbs` (warn/error) -
   * each wrap captures whatever the console method currently is at install
   * time and calls it at the end, so both run.
   */
  private installConsoleLogCapture(): () => void {
    if (typeof console === 'undefined') return () => undefined;

    const original = {
      trace: console.trace?.bind(console),
      debug: console.debug?.bind(console),
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };

    const formatArgs = (args: unknown[]): string =>
      args
        .map((a) => {
          if (typeof a === 'string') return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(' ')
        .slice(0, 5000);

    const wrap =
      (level: LogLevel, fn: (...args: unknown[]) => void) =>
      (...args: unknown[]) => {
        this.captureLog(level, formatArgs(args));
        fn(...args);
      };

    if (original.trace) console.trace = wrap('trace', original.trace);
    if (original.debug) console.debug = wrap('debug', original.debug);
    console.log = wrap('info', original.log);
    console.info = wrap('info', original.info);
    console.warn = wrap('warn', original.warn);
    console.error = wrap('error', original.error);

    this.log('autoCaptureLogs: console capture installed');

    return () => {
      if (original.trace) console.trace = original.trace;
      if (original.debug) console.debug = original.debug;
      console.log = original.log;
      console.info = original.info;
      console.warn = original.warn;
      console.error = original.error;
      this.log('autoCaptureLogs: console capture removed');
    };
  }

  /** Drains the queue, one request per 100 lines (the server's batch cap). */
  private async flushLogs(useBeacon = false): Promise<void> {
    while (this.logQueue.length > 0) {
      const batch = this.logQueue.splice(0, 100);
      await this.sendLogBatch(batch, useBeacon);
    }
  }

  private async sendLogBatch(batch: LogPayload[], useBeacon: boolean): Promise<void> {
    const body = JSON.stringify({ logs: batch });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(this.dsn.logsUrl, new Blob([body], { type: 'application/json' }));
      if (!ok) this.log(`Failed to beacon ${batch.length} log(s) on unload`);
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };
      if (typeof window !== 'undefined') fetchOptions.keepalive = true;

      const res = await fetch(this.dsn.logsUrl, fetchOptions);
      if (!res.ok) {
        this.log('Logs rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send logs -', (err as Error).message);
    }
  }

  // ─── Internal: tracing ────────────────────────────────────────────────────

  /** Drains the queue, one request per 20 transactions (the server's batch cap). */
  private async flushTransactions(useBeacon = false): Promise<void> {
    while (this.transactionQueue.length > 0) {
      const batch = this.transactionQueue.splice(0, 20);
      await this.sendTransactionBatch(batch, useBeacon);
    }
  }

  private async sendTransactionBatch(
    batch: TransactionPayload[],
    useBeacon: boolean,
  ): Promise<void> {
    const body = JSON.stringify({ transactions: batch });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        this.dsn.transactionsUrl,
        new Blob([body], { type: 'application/json' }),
      );
      if (!ok) this.log(`Failed to beacon ${batch.length} transaction(s) on unload`);
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };
      if (typeof window !== 'undefined') fetchOptions.keepalive = true;

      const res = await fetch(this.dsn.transactionsUrl, fetchOptions);
      if (!res.ok) {
        this.log('Transactions rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send transactions -', (err as Error).message);
    }
  }

  // ─── Internal: sessions ───────────────────────────────────────────────────

  private startSession(): void {
    this.sessionId = uuid();
    this.sessionStartedAt = new Date().toISOString();
    this.sessionStatus = 'ok';
    this.sessionErrorCount = 0;
    this.queueSessionUpdate({});
  }

  /**
   * Called from `captureException()`. Escalates the session's status - never
   * ends it, since a handled error doesn't mean the app stopped running.
   * `fatal` (from `installErrorHandlers()`'s uncaught/unhandled paths) marks
   * the session "crashed" and flushes immediately rather than waiting for
   * the timer, since the process may be about to exit.
   */
  private recordSessionError(fatal: boolean): void {
    if (!this.sessionId) return;

    this.sessionErrorCount++;
    const candidate: SessionStatus = fatal ? 'crashed' : 'errored';
    if (SESSION_SEVERITY[candidate] > SESSION_SEVERITY[this.sessionStatus]) {
      this.sessionStatus = candidate;
    }
    this.queueSessionUpdate({});

    if (fatal) void this.flushSessions();
  }

  private endSession(): void {
    if (!this.sessionId) return;
    this.queueSessionUpdate({ ended_at: new Date().toISOString() });
    this.sessionId = undefined;
  }

  private queueSessionUpdate(overrides: Partial<SessionPayload>): void {
    if (!this.sessionId || !this.sessionStartedAt) return;

    this.sessionQueue.push({
      session_id: this.sessionId,
      started_at: this.sessionStartedAt,
      status: this.sessionStatus,
      errors: this.sessionErrorCount,
      environment: this.opts.environment,
      release: this.opts.release,
      user_id: this.user.id,
      ...overrides,
    });
  }

  /** Drains the queue, one request per 100 updates (the server's batch cap). */
  private async flushSessions(useBeacon = false): Promise<void> {
    while (this.sessionQueue.length > 0) {
      const batch = this.sessionQueue.splice(0, 100);
      await this.sendSessionBatch(batch, useBeacon);
    }
  }

  private async sendSessionBatch(batch: SessionPayload[], useBeacon: boolean): Promise<void> {
    const body = JSON.stringify({ sessions: batch });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        this.dsn.sessionsUrl,
        new Blob([body], { type: 'application/json' }),
      );
      if (!ok) this.log(`Failed to beacon ${batch.length} session update(s) on unload`);
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };
      if (typeof window !== 'undefined') fetchOptions.keepalive = true;

      const res = await fetch(this.dsn.sessionsUrl, fetchOptions);
      if (!res.ok) {
        this.log('Sessions rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send sessions -', (err as Error).message);
    }
  }

  // ─── Internal: web vitals ─────────────────────────────────────────────────

  /**
   * Dynamically imported so Node consumers (same bundle, no browser APIs)
   * never pay for parsing a browser-only library they can't use - this
   * method itself is only ever called when `typeof window !== 'undefined'`.
   */
  private installWebVitalsCapture(): void {
    import('web-vitals')
      .then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
        const report = (metric: WebVitalMetric) => (entry: { value: number }) => {
          this.queueWebVital(metric, entry.value);
        };
        onLCP(report('LCP'));
        onINP(report('INP'));
        onCLS(report('CLS'));
        onFCP(report('FCP'));
        onTTFB(report('TTFB'));
      })
      .catch((err) => {
        this.log('Failed to load web-vitals -', (err as Error).message);
      });
  }

  private queueWebVital(metric: WebVitalMetric, value: number): void {
    this.webVitalsQueue.push({
      metric,
      value,
      route: normalizeUrl(window.location.pathname),
      timestamp: new Date().toISOString(),
      environment: this.opts.environment,
      release: this.opts.release,
      user_id: this.user.id,
    });

    if (this.webVitalsQueue.length >= 20) {
      void this.flushWebVitals();
    }
  }

  /** Drains the queue, one request per 100 readings (the server's batch cap). */
  private async flushWebVitals(useBeacon = false): Promise<void> {
    while (this.webVitalsQueue.length > 0) {
      const batch = this.webVitalsQueue.splice(0, 100);
      await this.sendWebVitalBatch(batch, useBeacon);
    }
  }

  private async sendWebVitalBatch(batch: WebVitalPayload[], useBeacon: boolean): Promise<void> {
    const body = JSON.stringify({ vitals: batch });

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        this.dsn.webVitalsUrl,
        new Blob([body], { type: 'application/json' }),
      );
      if (!ok) this.log(`Failed to beacon ${batch.length} web vital reading(s) on unload`);
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };
      if (typeof window !== 'undefined') fetchOptions.keepalive = true;

      const res = await fetch(this.dsn.webVitalsUrl, fetchOptions);
      if (!res.ok) {
        this.log('Web vitals rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send web vitals -', (err as Error).message);
    }
  }

  // ─── Internal: session replay ─────────────────────────────────────────────

  /**
   * Checks the project's session-replay privacy allowlist for a user (see
   * `ProjectEntity.replayUserAllowlist` on the API). Fails *closed* - a
   * network error or non-200 response means "don't record", since the whole
   * point is that a disallowed user's DOM never gets captured, and a
   * transient check failure shouldn't be the thing that decides otherwise.
   * Exposed publicly so a caller can gate its own `startReplayNow()` call
   * too, not just rely on the automatic check inside `startReplay()`.
   */
  async isReplayAllowed(userId?: string): Promise<boolean> {
    const uid = userId ?? this.user.id;
    try {
      const url = uid ? `${this.dsn.replayAllowedUrl}&userId=${encodeURIComponent(uid)}` : this.dsn.replayAllowedUrl;
      const res = await fetch(url);
      if (!res.ok) return false;
      const data = (await res.json()) as { allowed?: boolean };
      return !!data.allowed;
    } catch (err) {
      this.log('Failed to check replay allowlist -', (err as Error).message);
      return false;
    }
  }

  /**
   * Dynamically imported, same reasoning as `installWebVitalsCapture()` -
   * `rrweb` assumes browser globals and this package ships one bundle for
   * both browser and Node, so a static import would force every Node
   * consumer to pay for parsing a library they can never use.
   *
   * Gated behind `isReplayAllowed()` - recording only actually starts once
   * the allowlist check resolves, so a disallowed user's DOM is never
   * captured to begin with (not just dropped after ingest).
   */
  private startReplay(): void {
    if (this.replayId || this.replayStarting) return;
    this.replayStarting = true;
    this.replayStopRequested = false;

    void this.isReplayAllowed()
      .then((allowed) => {
        this.replayStarting = false;
        // stopReplay() can't touch replayId while the check is still in
        // flight (nothing's recording yet), so it sets this flag instead -
        // check it here rather than `this.replayId`.
        if (!allowed || this.replayStopRequested) {
          if (!allowed) this.log('Session replay not started - user is not in the project replay allowlist');
          this.replayStopRequested = false;
          return;
        }

        this.replayId = uuid();
        this.replaySequence = 0;
        this.replayStartedAt = new Date().toISOString();

        import('rrweb')
          .then(({ record }) => {
            // Re-check in case stopReplay() ran while the import was in flight.
            if (!this.replayId) return;
            this.stopRecording = record({
              emit: (event) => this.queueReplayEvent(event),
              maskAllInputs: true,
              ...(this.opts.replayMaskTextSelector
                ? { maskTextSelector: this.opts.replayMaskTextSelector }
                : {}),
            });
          })
          .catch((err) => {
            this.log('Failed to load rrweb -', (err as Error).message);
            this.replayId = undefined;
          });
      })
      .catch(() => {
        this.replayStarting = false;
      });
  }

  private stopReplay(useBeacon = false): void {
    this.replayStopRequested = true;
    if (!this.replayId) return;
    this.stopRecording?.();
    this.stopRecording = undefined;
    const endingReplayId = this.replayId;
    this.replayId = undefined;
    this.queueReplayChunk({ replay_id: endingReplayId, ended_at: new Date().toISOString() }, useBeacon);
  }

  private queueReplayEvent(event: unknown): void {
    this.replayQueue.push(event);
    if (this.replayQueue.length >= 200) {
      void this.flushReplay();
    }
  }

  /** Drains the queue into one chunk, tagged with whatever's pending (e.g. an `ended_at` from `stopReplay()`). */
  private async flushReplay(useBeacon = false, overrides: Partial<ReplayChunkPayload> = {}): Promise<void> {
    if (this.replayQueue.length === 0 && Object.keys(overrides).length === 0) return;
    if (!this.replayId && Object.keys(overrides).length === 0) return;

    // Beacon sends (pagehide) fire-and-forget and must go out immediately -
    // the page is already unloading, there's nothing to serialize behind.
    // A normal fetch send waits for any in-flight chunk to land first, so a
    // busy page emitting events faster than one chunk round-trips never has
    // more than one replay request open at once.
    if (!useBeacon && this.replaySendInFlight) {
      this.replayFlushPending = true;
      return;
    }

    const events = this.replayQueue.splice(0, this.replayQueue.length);
    if (events.length === 0 && Object.keys(overrides).length === 0) return;

    const chunk = {
      replay_id: overrides.replay_id ?? this.replayId!,
      sequence: this.replaySequence++,
      events,
      ...(this.replaySequence === 1 ? { started_at: this.replayStartedAt, url: window.location.href } : {}),
      environment: this.opts.environment,
      release: this.opts.release,
      user_id: this.user.id,
      ...overrides,
    };

    if (useBeacon) {
      await this.sendReplayChunk(chunk, true);
      return;
    }

    this.replaySendInFlight = true;
    try {
      await this.sendReplayChunk(chunk, false);
    } finally {
      this.replaySendInFlight = false;
      if (this.replayFlushPending) {
        this.replayFlushPending = false;
        void this.flushReplay();
      }
    }
  }

  /** Queues an out-of-band chunk (e.g. the closing `ended_at` marker) through the same flush path. */
  private queueReplayChunk(overrides: Partial<ReplayChunkPayload>, useBeacon = false): void {
    void this.flushReplay(useBeacon, overrides);
  }

  private async sendReplayChunk(chunk: ReplayChunkPayload, useBeacon: boolean): Promise<void> {
    const body = JSON.stringify(chunk);

    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(this.dsn.replayUrl, new Blob([body], { type: 'application/json' }));
      if (!ok) this.log('Failed to beacon a replay chunk on unload');
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };
      // No `keepalive` here - that's only for requests that must survive page
      // teardown, already handled by the `sendBeacon` branch above. Setting it
      // on a normal in-page fetch instead exposes replay chunks (easily
      // several hundred KB+ for a full DOM snapshot) to Chrome's combined 64KB
      // body quota for keepalive requests - one oversized chunk gets stuck
      // against that quota and every keepalive request queued behind it on
      // this origin sits stuck too, never resolving.
      //
      // Replay chunks are still the largest payloads this SDK ever sends - a
      // request stuck on a slow network hop or a misbehaving proxy would
      // otherwise hold a browser connection slot open forever, and once
      // enough chunks queue up behind it, that can exhaust the per-origin
      // connection limit and stall every other request the page makes to the
      // same API, not just replay ones. Self-abort so a single bad chunk
      // can't cascade into that.
      if (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) {
        fetchOptions.signal = AbortSignal.timeout(this.opts.replayRequestTimeout);
      }

      const res = await fetch(this.dsn.replayUrl, fetchOptions);
      if (!res.ok) {
        this.log('Replay chunk rejected by server -', res.status, await res.text().catch(() => ''));
      }
    } catch (err) {
      this.log('Failed to send replay chunk -', (err as Error).message);
    }
  }

  // ─── Internal: transport ─────────────────────────────────────────────────

  private async send(event: EventPayload): Promise<void> {
    let finalEvent: EventPayload | false = event;

    if (this.opts.beforeSend) {
      finalEvent = this.opts.beforeSend(event);
      if (finalEvent === false) {
        this.log('Event dropped by beforeSend hook');
        return;
      }
    }

    this.log('Sending event:', finalEvent.event_id, finalEvent.level);

    let body: string;
    try {
      body = JSON.stringify(finalEvent);
    } catch (err) {
      console.warn('[BugCatch] Failed to serialize event:', err);
      return;
    }

    try {
      const fetchOptions: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      };

      // keepalive is browser-only; Node.js fetch (undici) enforces a 64 KB body
      // limit with keepalive: true, which silently drops large payloads.
      if (typeof window !== 'undefined') {
        fetchOptions.keepalive = true;
      }

      const res = await fetch(this.dsn.ingestUrl, fetchOptions);

      if (!res.ok) {
        console.warn(`[BugCatch] Ingest responded with ${res.status}:`, await res.text());
      }
    } catch (err) {
      console.warn('[BugCatch] Failed to send event:', err);
    }
  }

  private log(...args: unknown[]): void {
    if (this.opts.debug) {
      console.debug('[BugCatch]', ...args);
    }
  }
}
